if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.refreshElements();
        this.initVariantMediaFilter();
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      refreshElements() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        this.refreshElements();
        const activeMedia =
          this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer?.querySelector('[data-media-id]');
        if (!activeMedia) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail?.parentElement?.firstChild !== activeThumbnail &&
              activeThumbnail?.parentElement?.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset?.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion || !position) return;
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initVariantMediaFilter() {
        this.productInfo = this.closest('product-info');
        this.colorOptionIndex = parseInt(this.dataset.colorOptionIndex, 10);
        if (!this.productInfo || !this.colorOptionIndex) return;
        this.variantSelects = this.productInfo.querySelector('variant-selects');
        if (!this.variantSelects) return;

        // FIX: Delay initial filter to allow Dawn to finish its own gallery setup first
        setTimeout(() => this.applyVariantMediaFilter(), 200);

        // FIX: Listen on productInfo instead of variantSelects directly.
        // Dawn's section rendering replaces the variant-selects element after each colour
        // change, which orphans any listener attached directly to it. productInfo is the
        // stable outer container that survives section renders, so events from the new
        // variant-selects still bubble up to it correctly.
        this.productInfo.addEventListener('change', (event) => {
          if (event.target.closest('variant-selects')) {
            setTimeout(() => this.applyVariantMediaFilter(), 150);
          }
        });
      }

      getSelectedOptionValue(optionIndex) {
        // FIX: Always fresh-query variant-selects from the DOM rather than using the cached
        // reference, in case Dawn's section rendering has replaced the element since init.
        const variantSelects = this.productInfo?.querySelector('variant-selects') || this.variantSelects;
        const index = optionIndex - 1;
        const selects = variantSelects.querySelectorAll('select');
        if (selects.length) return selects[index]?.value;
        const fieldsets = variantSelects.querySelectorAll('fieldset');
        if (!fieldsets.length) return null;
        return fieldsets[index]?.querySelector('input:checked')?.value || null;
      }

      mediaColorMatches(mediaColor, selected) {
        if (!mediaColor) return true;
        return mediaColor.trim().toLowerCase() === selected;
      }

      applyVariantMediaFilter() {
        this.refreshElements();
        if (!this.elements.viewer) return;
        const selectedValue = this.getSelectedOptionValue(this.colorOptionIndex);
        if (!selectedValue) {
          this.showAllMedia();
          return;
        }
        const selected = selectedValue.trim().toLowerCase();
        // FIX: Target only <li> slider items, not nested <button> modal-opener elements
        const viewerItems = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        const thumbItems = this.elements.thumbnails
          ? Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'))
          : [];
        let anyVisible = false;
        let anyTagged = false;
        viewerItems.forEach((item) => {
          const mediaColor = (item.dataset.mediaColor || '').trim().toLowerCase();
          if (mediaColor) anyTagged = true;
          const match = this.mediaColorMatches(mediaColor, selected);
          item.toggleAttribute('hidden', !match);
          item.classList.toggle('is-hidden', !match);
          if (match) anyVisible = true;
        });
        thumbItems.forEach((item) => {
          const mediaColor = (item.dataset.mediaColor || '').trim().toLowerCase();
          const match = this.mediaColorMatches(mediaColor, selected);
          item.toggleAttribute('hidden', !match);
          item.classList.toggle('is-hidden', !match);
        });
        // If nothing matched (or nothing is tagged), show all
        if (!anyVisible || !anyTagged) {
          this.showAllMedia();
          return;
        }
        // FIX: Only check <li> items for active state, not nested buttons
        const active = this.elements.viewer.querySelector('li.is-active');
        if (!active || active.hasAttribute('hidden')) {
          const firstVisible = viewerItems.find((item) => !item.hasAttribute('hidden'));
          if (firstVisible) this.setActiveMedia(firstVisible.dataset.mediaId, true);
        } else if (this.elements.thumbnails) {
          const activeThumb = this.elements.thumbnails.querySelector(
            `[data-target="${active.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(activeThumb);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        if (this.elements.thumbnails?.resetPages) this.elements.thumbnails.resetPages();

        // Preload the first image for each colour so future swatch clicks are instant
        this.preloadColourImages();
      }

      preloadColourImages() {
        if (!this.elements.viewer) return;
        const liItems = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        const seenColors = new Set();
        liItems.forEach((item) => {
          const color = (item.dataset.mediaColor || '').trim().toLowerCase();
          if (!color || seenColors.has(color)) return;
          seenColors.add(color);
          // Grab the img src and kick off a background download.
          // The browser caches it so when that colour is selected the image is instant.
          const img = item.querySelector('img[src]');
          if (img?.src) new Image().src = img.src;
        });
      }

      showAllMedia() {
        // FIX: Target only <li> slider items, not nested <button> modal-opener elements
        const viewerItems = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        const thumbItems = this.elements.thumbnails
          ? Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'))
          : [];
        viewerItems.forEach((item) => {
          item.removeAttribute('hidden');
          item.classList.remove('is-hidden');
        });
        thumbItems.forEach((item) => {
          item.removeAttribute('hidden');
          item.classList.remove('is-hidden');
        });
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        if (this.elements.thumbnails?.resetPages) this.elements.thumbnails.resetPages();
      }
    }
  );
}